import { QRPayload } from '@/types/qr';
import { isSafeUrl } from '@/lib/security/sanitizer';

export interface ValidationError {
  field?: string;
  message: string;
}

/**
 * Validates any QR payload and returns an array of validation errors.
 */
export function validatePayload(payload: QRPayload): ValidationError[] {
  const errors: ValidationError[] = [];

  switch (payload.type) {
    case 'url': {
      if (!payload.url || !payload.url.trim()) {
        errors.push({ field: 'url', message: 'URL cannot be empty.' });
      } else if (!isSafeUrl(payload.url)) {
        errors.push({ field: 'url', message: 'URL contains an unsafe protocol (e.g. javascript:).' });
      }
      break;
    }

    case 'text': {
      if (!payload.text || !payload.text.trim()) {
        errors.push({ field: 'text', message: 'Text content cannot be empty.' });
      }
      if (payload.text.length > 2953) {
        errors.push({ field: 'text', message: 'Text exceeds the maximum standard QR Code binary capacity (2953 bytes).' });
      }
      break;
    }

    case 'email': {
      if (!payload.email || !payload.email.trim()) {
        errors.push({ field: 'email', message: 'Email address is required.' });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email.trim())) {
        errors.push({ field: 'email', message: 'Please enter a valid email address.' });
      }
      break;
    }

    case 'phone': {
      if (!payload.phone || !payload.phone.trim()) {
        errors.push({ field: 'phone', message: 'Phone number is required.' });
      } else if (!/^[+0-9\s\-()]{3,25}$/.test(payload.phone.trim())) {
        errors.push({ field: 'phone', message: 'Please enter a valid phone number.' });
      }
      break;
    }

    case 'sms': {
      if (!payload.phone || !payload.phone.trim()) {
        errors.push({ field: 'phone', message: 'Recipient phone number is required.' });
      }
      break;
    }

    case 'wifi': {
      if (!payload.ssid || !payload.ssid.trim()) {
        errors.push({ field: 'ssid', message: 'Wi-Fi Network Name (SSID) is required.' });
      }
      if (payload.encryption !== 'nopass' && (!payload.password || payload.password.length < 8)) {
        errors.push({ field: 'password', message: 'WPA passwords must be at least 8 characters long.' });
      }
      break;
    }

    case 'vcard': {
      if (!payload.firstName || !payload.firstName.trim()) {
        errors.push({ field: 'firstName', message: 'First name is required for contact card.' });
      }
      if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email.trim())) {
        errors.push({ field: 'email', message: 'Please enter a valid email address.' });
      }
      break;
    }

    case 'geo': {
      const lat = Number(payload.latitude);
      const lng = Number(payload.longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.push({ field: 'latitude', message: 'Latitude must be a valid number between -90 and 90.' });
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.push({ field: 'longitude', message: 'Longitude must be a valid number between -180 and 180.' });
      }
      break;
    }

    case 'event': {
      if (!payload.title || !payload.title.trim()) {
        errors.push({ field: 'title', message: 'Event title is required.' });
      }
      if (!payload.startDate) {
        errors.push({ field: 'startDate', message: 'Event start date/time is required.' });
      }
      break;
    }

    case 'upi': {
      if (!payload.vpa || !payload.vpa.trim()) {
        errors.push({ field: 'vpa', message: 'UPI ID (VPA) is required (e.g. user@bank).' });
      } else if (!/^[\w.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(payload.vpa.trim())) {
        errors.push({ field: 'vpa', message: 'Invalid UPI ID format (expected format: username@bank).' });
      }
      if (!payload.payeeName || !payload.payeeName.trim()) {
        errors.push({ field: 'payeeName', message: 'Payee Name is required.' });
      }
      if (payload.amount && (isNaN(Number(payload.amount)) || Number(payload.amount) < 0)) {
        errors.push({ field: 'amount', message: 'Amount must be a positive number.' });
      }
      break;
    }

    case 'social': {
      if (!payload.usernameOrUrl || !payload.usernameOrUrl.trim()) {
        errors.push({ field: 'usernameOrUrl', message: 'Username or profile link is required.' });
      }
      break;
    }

    case 'crypto': {
      if (!payload.address || !payload.address.trim()) {
        errors.push({ field: 'address', message: 'Wallet address is required.' });
      }
      break;
    }

    case 'custom': {
      if (!payload.content || !payload.content.trim()) {
        errors.push({ field: 'content', message: 'Content cannot be empty.' });
      }
      break;
    }
  }

  return errors;
}
